import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { API, authFetch } from "@/App";
import {
  Users, CheckCircle, Clock, Video, Calendar, FileText,
  DollarSign, TrendingUp, Mic, Timer, BarChart3, CreditCard,
  Target, Upload, Send, BookOpen, Loader2
} from "lucide-react";

export const DashboardStats = ({
  clients,
  onboardedClients,
  pendingClients,
  upcomingMeetings,
  events
}) => {
  const [extraStats, setExtraStats] = useState(null);

  useEffect(() => {
    fetchExtraStats();
  }, []);

  const fetchExtraStats = async () => {
    try {
      const [leadsRes, invoicesRes, timeRes, blogRes] = await Promise.all([
        authFetch(`${API}/assessment/leads/stats`).catch(() => null),
        authFetch(`${API}/billing/invoices`).catch(() => null),
        authFetch(`${API}/billing/time-entries`).catch(() => null),
        fetch(`${API}/blog/posts?limit=1`).catch(() => null),
      ]);

      const data = {};
      if (leadsRes?.ok) data.leads = await leadsRes.json();
      if (invoicesRes?.ok) data.invoices = await invoicesRes.json();
      if (timeRes?.ok) data.time = await timeRes.json();
      if (blogRes?.ok) data.blog = await blogRes.json();
      setExtraStats(data);
    } catch (e) {
      console.error("Extra stats error:", e);
    }
  };

  const primaryStats = [
    { icon: Users, iconColor: "text-[#39FF14]", value: clients?.length || 0, label: "Total Clients" },
    { icon: CheckCircle, iconColor: "text-green-500", value: onboardedClients?.length || 0, label: "Onboarded" },
    { icon: Clock, iconColor: "text-amber-500", value: pendingClients?.length || 0, label: "Pending" },
    { icon: Video, iconColor: "text-blue-500", value: upcomingMeetings?.length || 0, label: "Meetings" },
    { icon: Calendar, iconColor: "text-purple-500", value: events?.length || 0, label: "Events" },
  ];

  return (
    <div className="space-y-6 mb-8">
      {/* Primary Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {primaryStats.map((stat, index) => (
          <Card key={index} className="border-neutral-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <stat.icon className={`w-5 h-5 ${stat.iconColor}`} />
              </div>
              <p className="text-3xl font-semibold text-neutral-900">{stat.value}</p>
              <p className="text-sm text-neutral-500">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Extended Stats — Billing, Leads, Time, Blog */}
      {extraStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Leads */}
          <Card className="border-neutral-200 bg-neutral-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-blue-500" />
                <span className="text-xs font-medium text-neutral-500 uppercase">Leads</span>
              </div>
              <p className="text-2xl font-bold text-neutral-900">{extraStats.leads?.total || 0}</p>
              <div className="flex items-center gap-3 mt-1 text-xs text-neutral-500">
                <span className="flex items-center gap-1"><Upload className="w-3 h-3" />{extraStats.leads?.imported_leads || 0} imported</span>
                <span>{extraStats.leads?.website_leads || 0} website</span>
              </div>
            </CardContent>
          </Card>

          {/* Invoices */}
          <Card className="border-neutral-200 bg-neutral-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-medium text-neutral-500 uppercase">Invoices</span>
              </div>
              <p className="text-2xl font-bold text-neutral-900">${(extraStats.invoices?.stats?.paid_amount || 0).toLocaleString()}</p>
              <div className="flex items-center gap-3 mt-1 text-xs text-neutral-500">
                <span>{extraStats.invoices?.stats?.total || 0} total</span>
                <span className="text-amber-600">${(extraStats.invoices?.stats?.outstanding || 0).toLocaleString()} outstanding</span>
              </div>
            </CardContent>
          </Card>

          {/* Time Today */}
          <Card className="border-neutral-200 bg-neutral-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Timer className="w-4 h-4 text-purple-500" />
                <span className="text-xs font-medium text-neutral-500 uppercase">Today</span>
              </div>
              <p className="text-2xl font-bold text-neutral-900">{extraStats.time?.today_total_hours || 0}h</p>
              <div className="mt-1 text-xs text-neutral-500">
                {extraStats.time?.active_entry
                  ? <span className="text-green-600 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />Clocked in</span>
                  : <span>Not clocked in</span>
                }
              </div>
            </CardContent>
          </Card>

          {/* Blog Posts */}
          <Card className="border-neutral-200 bg-neutral-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-4 h-4 text-rose-500" />
                <span className="text-xs font-medium text-neutral-500 uppercase">Blog</span>
              </div>
              <p className="text-2xl font-bold text-neutral-900">{extraStats.blog?.total || 0}</p>
              <div className="mt-1 text-xs text-neutral-500">
                <span>Published posts (auto-daily at 8am)</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Platform Updates Banner */}
      <Card className="border-[#39FF14]/20 bg-gradient-to-r from-[#333333] to-neutral-800">
        <CardContent className="p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-[#39FF14] text-black text-xs">Platform Updates</Badge>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-neutral-300">
                <span className="flex items-center gap-1"><CreditCard className="w-3 h-3 text-[#39FF14]" /> Billing & Invoicing</span>
                <span className="flex items-center gap-1"><Timer className="w-3 h-3 text-[#39FF14]" /> Time Tracking</span>
                <span className="flex items-center gap-1"><Mic className="w-3 h-3 text-[#39FF14]" /> Podcast Suite</span>
                <span className="flex items-center gap-1"><BookOpen className="w-3 h-3 text-[#39FF14]" /> Auto SEO Blog</span>
                <span className="flex items-center gap-1"><Upload className="w-3 h-3 text-[#39FF14]" /> CSV Lead Import</span>
                <span className="flex items-center gap-1"><Send className="w-3 h-3 text-[#39FF14]" /> Email Campaigns</span>
                <span className="flex items-center gap-1"><FileText className="w-3 h-3 text-[#39FF14]" /> Case Studies & FAQ</span>
                <span className="flex items-center gap-1"><DollarSign className="w-3 h-3 text-[#39FF14]" /> Consultant Payouts</span>
              </div>
            </div>
            <div className="text-xs text-neutral-500 flex-shrink-0">
              www.mybizconsulting.net
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardStats;
